import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, XCircle, AlertCircle, Info, X } from 'lucide-react';

/**
 * Toast notification component
 * Displays temporary notifications with different types and auto-dismiss
 */
const Toast = ({ 
  isVisible, 
  onClose, 
  type = 'success', 
  title, 
  message, 
  duration = 4000,
  position = 'top-right' 
}) => {
  useEffect(() => {
    if (isVisible && duration > 0) {
      const timer = setTimeout(() => {
        onClose();
      }, duration);
      
      return () => clearTimeout(timer);
    }
  }, [isVisible, duration, onClose]);

  const getTypeConfig = () => {
    switch (type) {
      case 'success':
        return {
          icon: CheckCircle,
          bgGradient: 'from-green-500/20 to-emerald-500/20',
          borderColor: 'border-green-500/30',
          iconColor: 'text-green-400',
          titleColor: 'text-green-300'
        };
      case 'error':
        return {
          icon: XCircle,
          bgGradient: 'from-red-500/20 to-pink-500/20',
          borderColor: 'border-red-500/30',
          iconColor: 'text-red-400',
          titleColor: 'text-red-300'
        };
      case 'warning':
        return {
          icon: AlertCircle,
          bgGradient: 'from-yellow-500/20 to-orange-500/20',
          borderColor: 'border-yellow-500/30',
          iconColor: 'text-yellow-400',
          titleColor: 'text-yellow-300'
        };
      case 'info':
      default:
        return {
          icon: Info,
          bgGradient: 'from-blue-500/20 to-indigo-500/20',
          borderColor: 'border-blue-500/30',
          iconColor: 'text-blue-400',
          titleColor: 'text-blue-300'
        };
    }
  };

  const getPositionClasses = () => {
    switch (position) {
      case 'top-left':
        return 'top-4 left-4';
      case 'top-center':
        return 'top-4 left-1/2 transform -translate-x-1/2';
      case 'top-right':
      default:
        return 'top-4 right-4';
      case 'bottom-left':
        return 'bottom-4 left-4';
      case 'bottom-center':
        return 'bottom-4 left-1/2 transform -translate-x-1/2';
      case 'bottom-right':
        return 'bottom-4 right-4';
    }
  };

  const typeConfig = getTypeConfig();
  const Icon = typeConfig.icon;

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, y: -20, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -20, scale: 0.9 }}
          transition={{ type: "spring", duration: 0.4 }}
          className={`fixed z-50 ${getPositionClasses()}`}
        >
          <div className={`
            relative max-w-sm w-full bg-gradient-to-br ${typeConfig.bgGradient} 
            backdrop-blur-xl border ${typeConfig.borderColor} rounded-xl shadow-2xl 
            overflow-hidden
          `}>
            {/* Animated background effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent animate-pulse" />
            
            <div className="relative p-4">
              <div className="flex items-start gap-3">
                {/* Icon */}
                <div className="flex-shrink-0">
                  <Icon className={`w-5 h-5 ${typeConfig.iconColor}`} />
                </div>
                
                {/* Content */}
                <div className="flex-1 min-w-0">
                  {title && (
                    <h4 className={`font-medium ${typeConfig.titleColor} mb-1`}>
                      {title}
                    </h4>
                  )}
                  <p className="text-sm text-gray-300 leading-relaxed">
                    {message}
                  </p>
                </div>
                
                {/* Close button */}
                <button
                  onClick={onClose}
                  className="flex-shrink-0 p-1 rounded-full hover:bg-white/10 text-gray-400 hover:text-white transition-colors duration-200"
                >
                  <X size={14} />
                </button>
              </div>
            </div>
            
            {/* Progress bar for auto-dismiss */}
            {duration > 0 && (
              <motion.div
                initial={{ width: '100%' }}
                animate={{ width: '0%' }}
                transition={{ duration: duration / 1000, ease: 'linear' }}
                className={`h-1 bg-gradient-to-r ${typeConfig.bgGradient} opacity-60`}
              />
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

/**
 * Hook for managing toast notifications
 */
export const useToast = () => {
  const [toasts, setToasts] = useState([]);

  const showToast = (config) => {
    const id = Date.now();
    const toast = { id, ...config };
    
    setToasts(prev => [...prev, toast]);
    
    // Auto-remove after duration
    if (config.duration !== 0) {
      setTimeout(() => {
        removeToast(id);
      }, config.duration || 4000);
    }
    
    return id;
  };

  const removeToast = (id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  const success = (title, message, options = {}) => 
    showToast({ type: 'success', title, message, ...options });

  const error = (title, message, options = {}) => 
    showToast({ type: 'error', title, message, ...options });

  const warning = (title, message, options = {}) => 
    showToast({ type: 'warning', title, message, ...options });

  const info = (title, message, options = {}) => 
    showToast({ type: 'info', title, message, ...options });

  return {
    toasts,
    showToast,
    removeToast,
    success,
    error,
    warning,
    info
  };
};

/**
 * Toast container component to render all active toasts
 */
export const ToastContainer = ({ toasts, onRemoveToast }) => {
  return (
    <div className="fixed inset-0 pointer-events-none z-50">
      {toasts.map((toast, index) => (
        <div
          key={toast.id}
          className="pointer-events-auto"
          style={{
            position: 'absolute',
            top: `${4 + index * 80}px`,
            right: '16px'
          }}
        >
          <Toast
            isVisible={true}
            onClose={() => onRemoveToast(toast.id)}
            type={toast.type}
            title={toast.title}
            message={toast.message}
            duration={0} // Managed by useToast hook
            position="top-right"
          />
        </div>
      ))}
    </div>
  );
};

export default Toast;
